/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useEffect, useState } from 'react'

import isEqual from 'lodash/isEqual'

/** How long the page has to sit unchanged after a view loads before that state counts as the view. */
export const SETTLE_MS = 600

/**
 * Whether the page has changed since the selected saved view was loaded or saved.
 *
 * It compares `current` against the page as it stood once the view finished loading, not against
 * the stored view: loading can reapply columns over what the company has now, switch panels on and
 * fill in defaults an older view lacks, so the live state rarely matches the stored one exactly.
 *
 * @param selected the selected view, or null
 * @param current what the page is compared on now (leave out what shouldn't count, like the
 *   viewport or pane sizes)
 * @param isSettling true while the page is still applying a view or loading what it needs
 */
export const useSettledDirty = <V extends { isDefault?: boolean }>(
  selected: V | null,
  current: unknown,
  isSettling: boolean
): boolean => {
  const [baseline, setBaseline] = useState<{ view: V; snapshot: unknown } | null>(null)

  const currentKey = JSON.stringify(current)
  // Flipping "open by default" stores a new object but not a new view, so it keeps the baseline.
  const hasBaseline =
    !!baseline &&
    !!selected &&
    (baseline.view === selected || isEqual({ ...baseline.view, isDefault: false }, { ...selected, isDefault: false }))

  // Restarted by every change, so it fires once the load has stopped moving things around.
  useEffect(() => {
    if (!selected || hasBaseline || isSettling) return
    const timer = setTimeout(() => setBaseline({ view: selected, snapshot: current }), SETTLE_MS)
    return () => clearTimeout(timer)
    // currentKey stands in for current, which is a new object every render.
  }, [selected, hasBaseline, isSettling, currentKey])

  return hasBaseline && !isEqual(current, baseline.snapshot)
}
